import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inCls, btnPrimary } from "./ui";

function Shell({ title, sub, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--ib-charcoal)] p-5">
      <div className="w-full max-w-md bg-white p-8 shadow-2xl">
        <img src="/media/logo.png" alt="Ironbark Construction" className="mx-auto h-12" />
        <h1 className="mt-6 text-center font-display text-2xl uppercase">{title}</h1>
        {sub && <p className="mt-1 text-center text-sm text-black/60">{sub}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-black/60">{label}</span>{children}</label>;
}

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [mode, setMode] = useState("login");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/setup-status").then((r) => r.data.needs_setup && nav("/admin/setup")); }, [nav]);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: f.email });
        toast.success("If that email has an account, a reset link is on its way.");
        setMode("login");
      } else {
        const { data } = await api.post("/auth/login", f);
        setUser(data);
        nav("/admin");
      }
    } catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };
  return (
    <Shell title={mode === "login" ? "Owner Login" : "Reset password"} sub={mode === "forgot" ? "Enter your email and we'll send you a reset link." : "Manage your website, projects, leads and AI assistant."}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <L label="Email"><input type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inCls} data-testid="login-email" /></L>
        {mode === "login" && <L label="Password"><input type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inCls} data-testid="login-password" /></L>}
        {err && <p className="bg-red-50 p-2 text-sm text-red-700" role="alert" data-testid="login-error">{err}</p>}
        <button className={`${btnPrimary} w-full justify-center py-3`} disabled={busy} data-testid="login-submit-btn">{mode === "login" ? "Sign in" : "Send reset link"}</button>
        <button type="button" onClick={() => setMode(mode === "login" ? "forgot" : "login")} className="w-full text-center text-sm text-black/60 hover:text-black" data-testid="login-toggle-forgot">{mode === "login" ? "Forgot your password?" : "Back to sign in"}</button>
        <Link to="/" className="block text-center text-xs text-black/40">← Back to website</Link>
      </form>
    </Shell>
  );
}

export function Setup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Passwords don't match");
    try {
      const { data } = await api.post("/auth/setup", f);
      setUser(data);
      nav("/admin");
    } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <Shell title="Welcome — create your owner login" sub="This one-time setup creates the main owner account for the Ironbark admin.">
      <form onSubmit={submit} className="space-y-4" data-testid="setup-form">
        <L label="Your name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inCls} data-testid="setup-name" /></L>
        <L label="Email"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inCls} data-testid="setup-email" /></L>
        <L label="Password (8+ characters)"><input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inCls} data-testid="setup-password" /></L>
        <L label="Confirm password"><input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={inCls} data-testid="setup-confirm" /></L>
        {err && <p className="bg-red-50 p-2 text-sm text-red-700">{err}</p>}
        <button className={`${btnPrimary} w-full justify-center py-3`} data-testid="setup-submit-btn">Create my account</button>
      </form>
    </Shell>
  );
}

export function TokenPassword({ mode }) {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const invite = mode === "invite";
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Passwords don't match");
    try {
      const body = { token: params.get("token"), password: f.password, name: f.name || undefined };
      if (invite) {
        const { data } = await api.post("/auth/accept-invite", body);
        setUser(data);
        nav("/admin");
      } else {
        await api.post("/auth/reset-password", body);
        toast.success("Password updated. Please sign in.");
        nav("/admin/login");
      }
    } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <Shell title={invite ? "Set up your admin login" : "Choose a new password"} sub={invite ? "Create your own secure password to start managing the Ironbark website." : ""}>
      <form onSubmit={submit} className="space-y-4" data-testid="token-password-form">
        {invite && <L label="Your name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inCls} data-testid="invite-name" /></L>}
        <L label="New password (8+ characters)"><input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inCls} data-testid="new-password" /></L>
        <L label="Confirm password"><input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={inCls} data-testid="confirm-password" /></L>
        {err && <p className="bg-red-50 p-2 text-sm text-red-700">{err}</p>}
        <button className={`${btnPrimary} w-full justify-center py-3`} data-testid="token-password-submit">{invite ? "Create my login" : "Update password"}</button>
      </form>
    </Shell>
  );
}
